import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, Car, Trash2, DollarSign, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

// Lavender theme tokens
const THEME = {
  primary: '#C4B5FD',
  light: '#EDE9FE',
  lighter: '#F5F3FF',
  dark: '#5B21B6',
  icon: '#8B5CF6',
  border: '#DDD6FE',
  surface: '#FAFAFD',
};

const HistoryTab = ({ vehicles }) => {
  const { isDemoMode } = useDemo();
  const [parkings, setParkings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [isDemoMode]);

  const fetchHistory = async () => {
    // In demo mode, return empty history (no ended parkings in demo)
    if (isDemoMode) {
      setParkings([]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/parking?status=ended');
      setParkings(response.data);
    } catch (error) {
      console.error('Failed to fetch parking history:', error);
      toast.error('Failed to load parking history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // In demo mode, just remove from state
    if (isDemoMode) {
      setParkings(prev => prev.filter(p => p.id !== id));
      toast.success('Parking record deleted');
      return;
    }
    
    try {
      await API.delete(`/parking/${id}`);
      toast.success('Parking record deleted');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to delete parking record');
    }
  };

  const handleRepark = async (parking) => {
    try {
      const newParking = {
        title: parking.title,
        vehicle_id: parking.vehicle_id,
        address: parking.address,
        place_name: parking.place_name,
        garage_name: parking.garage_name,
        level_floor: parking.level_floor,
        section_zone: parking.section_zone,
        landmarks: parking.landmarks,
        place_id: parking.place_id,
      };
      await API.post('/parking', newParking);
      toast.success('Reparked! Check Active tab.');
    } catch (error) {
      toast.error('Failed to create new parking');
    }
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : null;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDuration = (startStr, endStr) => {
    if (!endStr) return null;
    const start = new Date(startStr);
    const end = new Date(endStr);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-8">
        <h3 
          className="text-xl font-semibold"
          style={{ color: THEME.dark }}
        >
          Parking History
        </h3>
        <p className="text-sm text-gray-500 mt-1">Past parking sessions</p>
      </div>

      {parkings.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl shadow-sm"
          style={{ 
            backgroundColor: THEME.lighter,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <Clock className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No parking history</h4>
          <p className="text-gray-500 max-w-sm mx-auto">
            Your completed parkings will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {parkings.map((parking) => (
            <Card 
              key={parking.id} 
              className="overflow-hidden"
              style={{ 
                borderColor: '#E5E7EB',
                borderWidth: '1px',
                borderLeftWidth: '4px',
                borderLeftColor: '#D1D5DB',
                backgroundColor: '#F9FAFB',
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <h4 className="font-semibold text-gray-900">{parking.title}</h4>
                      {parking.ended_at && (
                        <Badge 
                          variant="outline" 
                          className="font-medium"
                          style={{ 
                            borderColor: '#E5E7EB',
                            color: '#6B7280',
                          }}
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          {getDuration(parking.parked_at, parking.ended_at)}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>{formatDate(parking.parked_at)}</span>
                        {parking.ended_at && (
                          <span className="text-gray-400"> → {formatDate(parking.ended_at)}</span>
                        )}
                      </div>
                      
                      {parking.vehicle_id && getVehicleName(parking.vehicle_id) && (
                        <div className="flex items-center gap-2">
                          <Car className="h-4 w-4 text-gray-400" />
                          {getVehicleName(parking.vehicle_id)}
                        </div>
                      )}
                      
                      {(parking.address || parking.place_name) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {parking.place_name || parking.address}
                        </div>
                      )}
                      
                      {parking.amount_paid && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          <span className="text-green-600">${parking.amount_paid}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRepark(parking)}
                      className="shadow-sm hover:shadow-md transition-shadow"
                      style={{ 
                        borderColor: THEME.border,
                        color: THEME.dark,
                      }}
                      data-testid={`repark-btn-${parking.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Repark
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(parking.id)}
                      className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-history-btn-${parking.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default HistoryTab;
